import { useEffect, useMemo, useState } from "react";
import { boxArt, look } from "@bd/art";
import { dateOf } from "@bd/sim";
import { CloseupCanvas, type Actor } from "../world/CloseupCanvas";
import { C } from "@bd/content";
import { Icon, Sheet } from "@bd/ui-kit";
import { me, useGame } from "../store";
import { cover } from "../ui/Hud";
import { sfx } from "../lib/sound";
import { ScoreBadge, Masthead } from "./Reviews";
import { t, tp, tx } from "../i18n";

/**
 * Launch day: the box lands on the shelf, each reviewer's score stamps in one at a time,
 * the verdict banner drops, and the first week's sales tick up. Then the full breakdown.
 */
export function Launch({ productId }: { productId: string }) {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const co = me(game)!;
  const p = co.products.find((x) => x.id === productId);
  const [shown, setShown] = useState(0);
  const [avg, setAvg] = useState(0);
  const reviews = p?.reviews ?? [];
  const all = shown >= reviews.length;
  useEffect(() => {
    if (!p || all) return;
    const t = setTimeout(() => {
      setShown((n) => n + 1);
      sfx("stamp");
    }, shown === 0 ? 900 : 750);
    return () => clearTimeout(t);
  }, [shown, all, p]);
  // the average counts up once every score is in
  useEffect(() => {
    if (!p || !all) return;
    let v = 0;
    const id = setInterval(() => {
      v = Math.min(p.score, v + p.score / 18);
      setAvg(v);
      if (v >= p.score) {
        clearInterval(id);
        sfx(p.score >= 8 ? "fanfare" : p.score >= 5 ? "chime" : "thud");
      }
    }, 40);
    return () => clearInterval(id);
  }, [all, p]);
  // then the first week's sales tick up like a till
  const firstWeek = p?.weekly[0] ?? 0;
  const [sold, setSold] = useState(0);
  const finished = all && !!p && avg >= p.score;
  useEffect(() => {
    if (!finished || !firstWeek) return;
    let v = 0;
    const id = setInterval(() => {
      v = Math.min(firstWeek, v + Math.max(1, Math.ceil(firstWeek / 30)));
      setSold(v);
      if (v % 5 === 0) sfx("click");
      if (v >= firstWeek) {
        clearInterval(id);
        sfx("cash");
      }
    }, 35);
    return () => clearInterval(id);
  }, [finished, firstWeek]);
  const quality = useGame((s) => s.quality);
  const display = useMemo(() => (p ? boxArt({ title: p.design.name, theme: p.design.theme, mechanic: p.design.mechanic, publisherColor: co.color, quality: p.q.art || 0.6, audience: p.design.audience }) : undefined), [p?.id]);
  // the crowd outside the shop grows with the first week's sales, and reacts to the verdict
  const crowd = Math.max(2, Math.min(12, Math.round(2 + 2.2 * Math.log10(Math.max(1, firstWeek / 60)))));
  const actors = useMemo<Actor[]>(() => {
    if (!p) return [];
    const out: Actor[] = [];
    for (let i = 0; i < crowd; i++) {
      const cheer = finished && p.score >= 8 && i % 2 === 0;
      const mouth = !finished ? (i % 3 === 0 ? "open" : "smile") : p.score >= 6.5 ? "grin" : p.score >= 5 ? "smile" : "flat";
      out.push({ id: `fan${i}`, look: look((p.id.length * 7 + i * 13 + 5) % 64), seat: i, armsUp: cheer, lean: 0, mouth, stand: true });
    }
    out.push({ id: "you", look: look(co.staff[0]?.look ?? 3), seat: -1, armsUp: finished && p.score >= 8, lean: 0, mouth: finished && p.score >= 6.5 ? "grin" : "smile" });
    return out;
  }, [p?.id, crowd, finished, p?.score, co.staff]);
  if (!p) return null;
  const verdict = p.score >= 8 ? t("Hit!") : p.score >= 6.5 ? t("Solid") : p.score >= 5 ? t("Mixed") : t("Flop");
  const tone = p.score >= 8 ? "var(--coral)" : p.score >= 6.5 ? "var(--green)" : p.score >= 5 ? "var(--warn)" : "var(--bad)";
  const done = finished;
  return (
    <>
    <CloseupCanvas kind="launch" year={dateOf(game.week).year} actors={actors} quality={quality} display={display} mood="golden" />
    <Sheet drawer title={t("Launch day: {name}", { name: p.design.name })} onClose={() => set({ screen: null })} wide testId="launch"
      footer={<>
        <button className="btn primary" onClick={() => set({ screen: { kind: "reviews", productId } })} data-testid="launch-reviews">{t("Read the reviews")}</button>
      </>}>
      <div className="launch">
        <div className="launch-shelf">
          <img src={cover(p.design.name, p.design.theme, p.design.mechanic, co.color, p.q.art || 0.6, 2, false, p.design.audience)} alt={t("{name} box", { name: p.design.name })} className="launch-box" />
          <div className="launch-plank" />
          {done && p.score >= 8 && <div className="confetti" aria-hidden="true">{Array.from({ length: 24 }, (_, i) => <span key={i} style={{ left: `${(i * 37) % 100}%`, animationDelay: `${(i % 6) * 0.12}s`, background: ["#E85848", "#22968C", "#DEA440", "#5A8CDC"][i % 4] }} />)}</div>}
        </div>
        <div className="stack" style={{ gap: 8, minWidth: 0 }}>
          {reviews.map((r, i) => (
            <div key={r.reviewer} className={`card spread launch-review ${i < shown ? "in" : ""}`} style={{ padding: "8px 12px" }}>
              <span className="row" style={{ minWidth: 0, gap: 8 }}>
                <Masthead id={r.reviewer} size={30} />
                <span style={{ minWidth: 0 }}>
                  <strong>{C.reviewer[r.reviewer]?.name ?? r.reviewer}</strong>
                  <span className="muted ellipsis" style={{ display: "block", fontSize: "0.82rem" }}>“{tx(r.quote)}”</span>
                </span>
              </span>
              {i < shown ? <span className="stamp"><ScoreBadge score={r.score} compact /></span> : <span className="muted">…</span>}
            </div>
          ))}
          <div className="spread" style={{ marginTop: 6 }}>
            <span className="display row" style={{ fontSize: "1.5rem", gap: 6 }} aria-label={t("Average {score}", { score: all ? avg.toFixed(1) : "…" })} title={t("Average {score}", { score: all ? avg.toFixed(1) : "…" })}><Icon name="star" size={22} color="var(--gold)" />{all ? avg.toFixed(1) : "…"}</span>
            {done && <span className="display launch-verdict" style={{ color: tone }}>{verdict}</span>}
          </div>
          {done && (
            <span className="spread card" style={{ padding: "8px 12px" }} data-testid="launch-sales">
              <span className="muted row" style={{ gap: 6 }} title={t("First week in shops")}><Icon name="box" size={18} />{t("Week {n}", { n: 1 })}</span>
              <span className="display" style={{ fontSize: "1.4rem" }}>{firstWeek ? tp(sold, "{n} copy", "{n} copies") : t("going out now")}</span>
            </span>
          )}
        </div>
      </div>
    </Sheet>
    </>
  );
}
